import { ApiClient } from './apiClient';

// Organization types
export interface Organization {
  id: number;
  name: string;
  type: string;
  parent_org_id: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // KAN-85: organization/reseller logo. logo_s3_key is the raw S3 key; logoUrl
  // is the derived (presigned) URL the frontend renders, or null when unset.
  logo_s3_key?: string | null;
  logoUrl?: string | null;
}

export interface CreateOrganizationData {
  name: string;
  type: string;
  parent_org_id?: number;
}

export interface UpdateOrganizationData {
  name?: string;
  type?: string;
  parent_org_id?: number | null;
}

// User types
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  department: string | null;
  // KAN-38: SIM-visibility flags. Optional in the type so older API responses
  // (pre-Phase-2) parse without warnings; new code should treat them as required.
  can_view_subordinate_sims?: boolean;
  can_view_yacht_sims?: boolean;
  organization_id: number;
  org_name: string;
  is_active: boolean;
  last_login: string | null;
  /** Cognito status is FORCE_CHANGE_PASSWORD: invited, never accepted. */
  invite_pending?: boolean;
  /** Null once the user is permanently deleted; the row stays for audit history. */
  cognito_sub?: string | null;
  /** Cognito Username the user signs in with; null if the Cognito record is gone. */
  cognito_username?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId: number;
  department?: string;
  canViewSubordinateSims?: boolean;
  canViewYachtSims?: boolean;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  role?: string;
  organizationId?: number;
  department?: string;
  canViewSubordinateSims?: boolean;
  canViewYachtSims?: boolean;
}

// Lookup types
export interface LookupValue {
  id: number;
  category: string;
  value: string;
  label: string;
  is_system: boolean;
  sort_order: number;
}

// Asset types
export interface Asset {
  id: number;
  name: string;
  organization_id: number;
  org_name: string;
  asset_type: string;
  settings: Record<string, any> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // KAN-19: vessel logo. logo_s3_key is the raw S3 object key; logoUrl is the
  // derived (e.g. presigned/CDN) URL the frontend renders, or null when unset.
  logo_s3_key?: string | null;
  logoUrl?: string | null;
}

// KAN-19: vessel logo upload responses
export interface LogoUploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface SetAssetLogoResponse {
  success: true;
  logoUrl: string;
}

export interface CreateAssetData {
  name: string;
  organizationId: number;
  asset_type?: string;
  settings?: Record<string, any>;
}

export interface UpdateAssetData {
  name?: string;
  asset_type?: string;
  settings?: Record<string, any>;
}

// SIM types
export interface UnifiedSim {
  identifier: string;
  provider: string;
  msisdn: string | null;
  status: string;
  product: string | null;
  alias: string | null;
  // KAN-86: eSIM identity (VRS/Data provider only). isEsim is derived from eid
  // presence; eid is the eUICC ID, rspState the Remote SIM Provisioning state.
  eid?: string | null;
  rspState?: string | null;
  // Client-supplied label given at provisioning (echoed by VRS).
  customReference?: string | null;
  isEsim?: boolean;
  // KAN-94: when the SIM entered our DB (VRS provider). For a provisioned eSIM
  // this is the provisioning time, so the inventory can be sorted to find it.
  createdAt?: string | null;
  allocation: SimAllocation | null;

  // KAN-105: Starlink terminal fields, present only when provider === 'clarus'.
  // Terminals share this type so the inventory can render every connectivity
  // type from one list; the Starlink column set reads these.
  resourceType?: 'sim' | 'terminal';
  deviceId?: string;
  serviceLineNumber?: string | null;
  clientName?: string | null;
  siteName?: string | null;
  kitSerialNumber?: string | null;
  dishSerialNumber?: string | null;
  isOnline?: boolean;
  runningSoftwareVersion?: string | null;
  lastTelemetryAt?: string | null;
  signalQuality?: number | null;
  uptimeSeconds?: number | null;
  activeAlerts?: TerminalAlert[];
  lastSyncedAt?: string | null;
}

export interface TerminalAlert {
  code: number;
  label: string;
}

export interface SimAllocation {
  assignmentId: number;
  organizationId: number;
  orgName: string;
  assetId: number | null;
  assetName: string | null;
  userId: number | null;
  userName: string | null;
  simType: string | null;
  friendlyName: string | null;
}

export interface AllocateSimData {
  identifier: string;
  provider: string;
  organizationId: number;
  friendly_name?: string | null;
}

export interface AssignSimToAssetData {
  identifier: string;
  provider: string;
  assetId: number;
  friendly_name?: string | null;
}

export interface AssignSimToUserData {
  identifier: string;
  provider: string;
  userId: number;
  // KAN-105: required for SIMs, omitted for Starlink terminals — sim_type
  // classifies a SIM and has no meaning for a terminal.
  simType?: string;
  friendly_name?: string | null;
}

// Audit log types
export interface AuditLogEntry {
  id: number;
  user_id: number | null;
  user_email: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, any> | null;
  ip_address: string | null;
  created_at: string;
}

// Me response
export interface MeUser {
  id: number;
  cognitoSub: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  department: string | null;
  canViewSubordinateSims?: boolean;
  canViewYachtSims?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MeOrganization {
  id: number;
  name: string;
  type: string;
  parentOrgId: number | null;
}

export interface RecentLogin {
  at: string;
  ip: string | null;
  user_agent: string | null;
}

export interface MeResponse {
  user: MeUser;
  organization: MeOrganization;
  role: string;
  /** Last 10 sign-ins, newest first (KAN-163). */
  recent_logins?: RecentLogin[];
}

// Organization endpoints
export const getOrganizations = async (): Promise<Organization[]> => {
  const res = await ApiClient.get<{ organizations: Organization[] }>('/admin/organizations');
  return res.organizations;
};

export const getOrganization = async (id: number): Promise<Organization> => {
  const res = await ApiClient.get<{ organization: Organization }>(`/admin/organizations/${id}`);
  return res.organization;
};

export const createOrganization = (data: CreateOrganizationData): Promise<Organization> =>
  ApiClient.post<Organization>('/admin/organizations', data);

export const updateOrganization = (
  id: number,
  data: UpdateOrganizationData
): Promise<Organization> => ApiClient.put<Organization>(`/admin/organizations/${id}`, data);

export const deleteOrganization = (id: number): Promise<void> =>
  ApiClient.delete<void>(`/admin/organizations/${id}`);

// User endpoints
export const getUsers = async (opts?: { includeInactive?: boolean }): Promise<User[]> => {
  const url = opts?.includeInactive ? '/admin/users?include_inactive=true' : '/admin/users';
  const res = await ApiClient.get<{ users: User[] }>(url);
  return res.users;
};

export const getUser = async (id: number): Promise<User> => {
  const res = await ApiClient.get<{ user: User }>(`/admin/users/${id}`);
  return res.user;
};

// KAN-180: `invite` says whether the activation email went out ('sent') or
// must be resent from the Users page ('email_failed'); the user exists either way.
export const createUser = (
  data: CreateUserData
): Promise<User & { invite?: 'sent' | 'email_failed' }> =>
  ApiClient.post<User & { invite?: 'sent' | 'email_failed' }>('/admin/users', data);

export const updateUser = (id: number, data: UpdateUserData): Promise<User> =>
  ApiClient.put<User>(`/admin/users/${id}`, data);

export const deleteUser = (id: number): Promise<void> =>
  ApiClient.delete<void>(`/admin/users/${id}`);

export const syncCognitoUsers = (): Promise<{
  message: string;
  totalCognito: number;
  alreadyInDb: number;
  imported: number;
  skippedTestUsers: number;
}> => ApiClient.post('/admin/users/sync', {});

// Lookup endpoints
export const getLookups = async (category?: string): Promise<LookupValue[]> => {
  const url = category ? `/admin/lookups?category=${category}` : '/admin/lookups';
  const res = await ApiClient.get<any>(url);
  // If category specified, returns { lookups: [...] } as flat array
  // If no category, returns { lookups: { category: [...] } } as grouped
  if (category) return res.lookups;
  // Flatten grouped lookups into single array
  const all: LookupValue[] = [];
  for (const items of Object.values(res.lookups) as LookupValue[][]) {
    all.push(...items);
  }
  return all;
};
export const createLookup = (data: {
  category: string;
  value: string;
  label: string;
}): Promise<any> => ApiClient.post('/admin/lookups', data);

// Asset endpoints
export const getAssets = async (): Promise<Asset[]> => {
  const res = await ApiClient.get<{ assets: Asset[] }>('/admin/assets');
  return res.assets;
};
export const getAsset = async (id: number): Promise<{ asset: Asset; simAssignments: any[] }> =>
  ApiClient.get(`/admin/assets/${id}`);
export const createAsset = (data: CreateAssetData): Promise<any> =>
  ApiClient.post('/admin/assets', data);
export const updateAsset = (id: number, data: UpdateAssetData): Promise<any> =>
  ApiClient.put(`/admin/assets/${id}`, data);
export const deleteAsset = (id: number): Promise<void> => ApiClient.delete(`/admin/assets/${id}`);

// KAN-19: vessel logo endpoints
export const getLogoUploadUrl = (
  assetId: number,
  contentType: string
): Promise<LogoUploadUrlResponse> =>
  ApiClient.post<LogoUploadUrlResponse>(`/admin/assets/${assetId}/logo/upload-url`, {
    contentType,
  });

export const setAssetLogo = (assetId: number, s3Key: string): Promise<SetAssetLogoResponse> =>
  ApiClient.put<SetAssetLogoResponse>(`/admin/assets/${assetId}/logo`, { s3Key });

export const removeAssetLogo = (assetId: number): Promise<{ success: true }> =>
  ApiClient.delete<{ success: true }>(`/admin/assets/${assetId}/logo`);

// KAN-85: organization (reseller/customer) logo endpoints. Mirror the asset
// logo flow — presigned PUT, commit s3Key, remove.
export const getOrgLogoUploadUrl = (
  orgId: number,
  contentType: string
): Promise<LogoUploadUrlResponse> =>
  ApiClient.post<LogoUploadUrlResponse>(`/admin/organizations/${orgId}/logo/upload-url`, {
    contentType,
  });

export const setOrgLogo = (orgId: number, s3Key: string): Promise<SetAssetLogoResponse> =>
  ApiClient.put<SetAssetLogoResponse>(`/admin/organizations/${orgId}/logo`, { s3Key });

export const removeOrgLogo = (orgId: number): Promise<{ success: true }> =>
  ApiClient.delete<{ success: true }>(`/admin/organizations/${orgId}/logo`);
export const reactivateUser = (id: number): Promise<{ id: number; message: string }> =>
  ApiClient.post(`/admin/users/${id}/reactivate`, {});
export const resendUserInvite = (id: number): Promise<{ id: number; message: string }> =>
  ApiClient.post(`/admin/users/${id}/resend-invite`, {});
export const sendUserPasswordReset = (id: number): Promise<{ id: number; message: string }> =>
  ApiClient.post(`/admin/users/${id}/password-reset`, {});
export const resetUserMfa = (id: number): Promise<{ id: number; message: string }> =>
  ApiClient.post(`/admin/users/${id}/reset-mfa`, {});
export const permanentDeleteUser = (id: number): Promise<{ id: number; message: string }> =>
  ApiClient.delete(`/admin/users/${id}/permanent`);
export const moveAssetToOrganization = (
  id: number,
  targetOrganizationId: number
): Promise<{
  message: string;
  asset_id: number;
  from_organization_id: number;
  to_organization_id: number;
  moved_sims: number;
  moved_users: number;
}> =>
  ApiClient.put(`/admin/assets/${id}/organization`, {
    target_organization_id: targetOrganizationId,
  });

// SIM endpoints
export const getSims = async (params?: {
  provider?: string;
  status?: string;
  search?: string;
  limit?: number;
}): Promise<UnifiedSim[]> => {
  const query = new URLSearchParams();
  if (params?.provider) query.set('provider', params.provider);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  const res = await ApiClient.get<{ sims: UnifiedSim[] }>(`/admin/sims${qs ? '?' + qs : ''}`);
  return res.sims;
};
export const allocateSim = (data: AllocateSimData): Promise<any> =>
  ApiClient.post('/admin/sims/allocate', data);
export const assignSimToAsset = (data: AssignSimToAssetData): Promise<any> =>
  ApiClient.post('/admin/sims/assign-to-asset', data);
export const assignSimToUser = (data: AssignSimToUserData): Promise<any> =>
  ApiClient.post('/admin/sims/assign-to-user', data);
export const removeSimAssignment = (id: number): Promise<void> =>
  ApiClient.delete(`/admin/sims/assignment/${id}`);
export const updateSimFriendlyName = (
  assignmentId: number,
  friendlyName: string | null
): Promise<{ id: number; identifier: string; provider: string; friendly_name: string | null }> =>
  ApiClient.put(`/admin/sims/assignment/${assignmentId}`, { friendly_name: friendlyName });

export interface MySim {
  assignment_id: number;
  resource_identifier: string;
  provider: string;
  sim_type: string | null;
  friendly_name: string | null;
  asset_id: number | null;
  user_id: number | null;
  organization_id: number;
  asset_name: string | null;
  org_name: string;
  assigned_first_name: string | null;
  assigned_last_name: string | null;
}
export const getMySims = async (): Promise<MySim[]> => {
  const res = await ApiClient.get<{ sims: MySim[] }>('/admin/my-sims');
  return res.sims;
};
export const deallocateSim = (id: number): Promise<void> =>
  ApiClient.delete(`/admin/sims/deallocate/${id}`);

export const simOperation = (
  provider: string,
  identifier: string,
  operation: 'activate' | 'suspend' | 'resume' | 'terminate'
): Promise<any> => ApiClient.post(`/admin/sims/${provider}/${identifier}/${operation}`, {});

// Audit log types for paginated response
export interface AuditLogPagination {
  limit: number;
  offset: number;
  total: number;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  pagination: AuditLogPagination;
}

// Audit log endpoints
export const getAuditLog = async (params?: {
  action?: string;
  limit?: number;
  offset?: number;
}): Promise<AuditLogResponse> => {
  const query = new URLSearchParams();
  if (params?.action) query.set('action', params.action);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset !== undefined) query.set('offset', String(params.offset));
  const qs = query.toString();
  const res = await ApiClient.get<AuditLogResponse>(`/admin/audit-log${qs ? '?' + qs : ''}`);
  return res;
};

// Template types
export interface YachtTemplate {
  id: number;
  name: string;
  description: string | null;
  size_category: string | null;
  crew_structure: {
    positions: Array<{ role: string; department: string; count: number }>;
  } | null;
  yacht_sim_slots: number;
  management_settings: Record<string, any> | null;
}

// Template endpoints
export const getTemplates = async (): Promise<YachtTemplate[]> => {
  const res = await ApiClient.get<{ templates: YachtTemplate[] }>('/admin/templates');
  return res.templates;
};

// Me endpoint
export const getMe = (): Promise<MeResponse> => ApiClient.get<MeResponse>('/admin/me');

export interface UpdateMyProfileData {
  firstName?: string;
  lastName?: string;
}

export interface UpdateMyProfileResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export const updateMyProfile = (data: UpdateMyProfileData): Promise<UpdateMyProfileResponse> =>
  ApiClient.put<UpdateMyProfileResponse>('/admin/me', data);

export interface ChangeMyPasswordData {
  previousPassword: string;
  proposedPassword: string;
}

export const changeMyPassword = (data: ChangeMyPasswordData): Promise<{ message: string }> =>
  ApiClient.post<{ message: string }>('/admin/me/change-password', data);

export const recordLoginEvent = (): Promise<void> => ApiClient.post('/admin/login', {});

// KAN-94: eSIM provisioning (VRS/Data provider, Travel prepaid pool). These call
// the vrs lambda directly (/vrs/*). Backend enforces reseller_admin+.
export interface ProvisionEsimData {
  mode?: '2-step' | '1-step';
  bundleId?: string;
  roamingControl?: { id?: number | string; group?: string };
  customReference?: string;
}

// KAN-95: provisioning is asynchronous — the call returns an acknowledgement and
// the eSIM (ICCID/QR) arrives later via the provider webhook. `iccid` is normally
// null here (populated only in the rare synchronous case).
export interface ProvisionEsimResult {
  message: string;
  submitted: boolean;
  ackId: string | null;
  iccid: string | null;
  bundleId: string | null;
  // KAN-167: what the server validated and sent to the provider.
  bundle?: {
    id: string;
    name: string;
    data: string | null;
    validity: number | null;
    balanceType: EsimBalanceType;
  } | null;
  roaming?: { id: number; group: string; name: string | null } | null;
}

export const provisionEsim = (data: ProvisionEsimData): Promise<ProvisionEsimResult> =>
  ApiClient.post<ProvisionEsimResult>('/vrs/esim/provision', data);

export interface EsimQrResult {
  iccid: string;
  contentType: string;
  dataUrl: string;
}

export const getEsimQrCode = (iccid: string): Promise<EsimQrResult> =>
  ApiClient.get<EsimQrResult>(`/vrs/esim/${iccid}/qrcode`);

export interface EsimCodesResult {
  iccid: string;
  activationCode?: string | null;
  confirmationCode?: string | null;
  ccEnabled?: boolean;
  rspState?: string | null;
  [key: string]: unknown;
}

export const getEsimCodes = (iccid: string): Promise<EsimCodesResult> =>
  ApiClient.get<EsimCodesResult>(`/vrs/esim/${iccid}/codes`);

// KAN-167: the Travel bundle catalogue, mirrored server-side from Vodafone's
// account catalogue. `balanceType` is the plain-language reading of the FUP QoS
// class (what happens once the allowance is used up).
export interface EsimBalanceType {
  key: 'standard' | 'throttled-1mbps' | 'throttled-5mbps' | 'throttled' | 'unknown';
  label: string;
  description: string;
}

export interface EsimBundle {
  id: string;
  productId: string;
  productName: string | null;
  name: string;
  bundleClass: string | null;
  usageType: string | null;
  fupQos: string | null;
  balanceType: EsimBalanceType;
  valueMb: number | null;
  // Human data size (e.g. "50 GB").
  data: string | null;
  // Validity in days.
  validity: number | null;
  defaultRoaming: { id: number; group: string } | null;
  retired: boolean;
  syncedAt: string | null;
  // 'travel-sim' = choose the coverage yourself; 'travel-zone' = legacy product
  // with a fixed zone baked in.
  family: 'travel-sim' | 'travel-zone';
}

export interface EsimBundlesResult {
  source: 'catalogue' | 'none';
  syncedAt: string | null;
  bundles: EsimBundle[];
}

export const getEsimBundles = (refresh = false): Promise<EsimBundlesResult> =>
  ApiClient.get<EsimBundlesResult>(`/vrs/esim/bundles${refresh ? '?refresh=true' : ''}`);

export interface RoamingProfile {
  group: string;
  id: number;
  name: string | null;
  region: string | null;
  zoneMapping: string | null;
  kind: 'regional' | 'single-country' | 'legacy-zone';
  // Countries covered, null when Vodafone publishes no list (legacy zones).
  countryCount: number | null;
}

export const getRoamingProfiles = (): Promise<{ profiles: RoamingProfile[] }> =>
  ApiClient.get<{ profiles: RoamingProfile[] }>('/vrs/esim/roaming-profiles');

export interface CoverageResult {
  group: string;
  id: number;
  name: string | null;
  region: string | null;
  zoneMapping: string | null;
  tier: number | null;
  countries: string[];
  available: boolean;
  note: string | null;
}

export const getCoverage = (
  group: string,
  id: number,
  signal?: AbortSignal
): Promise<CoverageResult> =>
  ApiClient.get<CoverageResult>(
    `/vrs/esim/coverage?group=${encodeURIComponent(group)}&id=${id}`,
    signal
  );

export interface LiveBundleBalance {
  fupQos: string | null;
  balanceType: EsimBalanceType;
  bundleType: string | null;
  remainingMb: number | null;
  expiry: string | null;
  isDepleted: boolean;
  isExpired: boolean;
  roamingId: number | null;
  roamingGroup: string | null;
}

export interface LocalBundle {
  id: number;
  bundleId: string | null;
  name: string | null;
  type: string | null;
  fupQos: string | null;
  balanceType: EsimBalanceType;
  fupMode: string | null;
  roaming: { group: string; id: number | null; name?: string | null } | null;
  totalMb: number | null;
  remainingMb: number | null;
  isDepleted: boolean | null;
  isExpired: boolean | null;
  balanceCheckedAt: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
}

export interface EsimBundleStatus {
  iccid: string;
  checkedAt: string | null;
  cached: boolean;
  providerError: string | null;
  live: LiveBundleBalance[] | null;
  bundles: LocalBundle[];
}

export const getEsimBundleStatus = (
  iccid: string,
  refresh = false,
  signal?: AbortSignal
): Promise<EsimBundleStatus> =>
  ApiClient.get<EsimBundleStatus>(
    `/vrs/esim/${iccid}/bundle${refresh ? '?refresh=true' : ''}`,
    signal
  );

export interface TopUpEsimData {
  bundleId: string;
  roamingControl?: { id: number; group: string };
}

export interface TopUpEsimResult {
  message: string;
  iccid: string;
  bundle: {
    id: string;
    name: string;
    data: string | null;
    validity: number | null;
    balanceType: EsimBalanceType;
  };
  roaming: { id: number; group: string; name: string | null };
}

export const topUpEsim = (iccid: string, data: TopUpEsimData): Promise<TopUpEsimResult> =>
  ApiClient.post<TopUpEsimResult>(`/vrs/esim/${iccid}/top-up`, data);

export interface ProviderTopUp {
  bundleId: string | null;
  requestType: string | null;
  valueMb: number | null;
  data: string | null;
  bundleClass: string | null;
  fupQos: string | null;
  executedAt: string | null;
  validityDays: number | null;
}

export interface TopUpHistory {
  iccid: string;
  provider: ProviderTopUp[];
  providerError: string | null;
  local: LocalBundle[];
}

export const getEsimTopUps = (iccid: string, signal?: AbortSignal): Promise<TopUpHistory> =>
  ApiClient.get<TopUpHistory>(`/vrs/esim/${iccid}/top-ups`, signal);

// KAN-20: vessels (assets) the current user is assigned to, scoped server-side
// to the caller. Used for vessel logo branding in the header.
export interface Vessel {
  id: number;
  name: string;
  logoUrl: string | null;
}

export const getMyVessels = async (): Promise<Vessel[]> => {
  const res = await ApiClient.get<{ vessels: Vessel[] }>('/admin/me/vessels');
  return res.vessels;
};

// KAN-85: the resolved header branding logo for the current user, cascading
// vessel → organization → reseller server-side. `branding` is null when nothing
// resolves (header then falls back to the fixed MIMO logo).
export interface Branding {
  source: 'vessel' | 'org' | 'reseller';
  name: string;
  logoUrl: string | null;
}

export const getMyBranding = async (): Promise<Branding | null> => {
  const res = await ApiClient.get<{ branding: Branding | null }>('/admin/me/branding');
  return res.branding;
};
